import React from 'react';
import Link from 'next/link';
import classNames from 'classnames';
import SvgArrow from '@/assets/svg/icon-arrow.svg';
import ContentfulRichText from '@/components/ContentfulRichText/ContentfulRichText';
import WistiaVideoBanner from '@/components/VideoCard/WistiaVideo/WistiaVideoBanner';
import WistiaVideoBase from '@/components/VideoCard/WistiaVideo/WistiaVideoBase';
import { composeImageUrl } from '@/utils/imageAsset';
import { DescriptionType, ImageType } from '@/utils/common.types';
import {
  heroWaveSizeMap,
  bgColorMap,
  videoHeroSizeMap,
} from '@/utils/styleHelpers';

interface Props {
  title: string;
  description: DescriptionType;
  isHeroTextHidden: boolean;
  isHeroTheaterMode: boolean;
  isHeroWaveOutline?: boolean;
  heroWaveOutlineHeight?: number;
  video?: {
    host: string;
    id: string;
    style: {
      maxHeight: string;
    };
  };
  videoMaxHeight?: string;
  image?: {
    desktop: ImageType;
    tablet: ImageType;
    mobile: ImageType;
  };
  buttonLabel: string;
  buttonUrl: string;
  backgroundColor: string;
  isStoryBook?: boolean;
}

const Hero = ({
  title,
  description,
  isHeroTextHidden,
  isHeroTheaterMode,
  isHeroWaveOutline,
  heroWaveOutlineHeight = 5,
  video,
  videoMaxHeight = video?.style?.maxHeight,
  image,
  buttonLabel,
  buttonUrl,
  backgroundColor = 'black',
  isStoryBook,
}: Props) => {
  const getTextOverlay = () => {
    if (isHeroTextHidden || !title) {
      return;
    }

    return (
      <div className="absolute bottom-0 left-0 right-0 top-1/2 bg-clayton-blue/80 md:left-1/2 md:top-0">
        <div className="mx-6 flex h-full flex-col items-center justify-center md:mx-12 mdDesktop:mx-[90px] mdDesktop:my-[18px]">
          <h1 className="ml-[-3px] pb-4 text-[16px] leading-[inherit] text-white md:text-[26px] mdDesktop:text-[36px] lgDesktop:ml-[-12px]">
            {title}
          </h1>
          {description && (
            <span className="text-[14px] text-white md:text-[18px] smDesktop:text-[20px]">
              {ContentfulRichText(description)}
            </span>
          )}
        </div>
      </div>
    );
  };

  const getCTAButton = () => {
    return (
      buttonLabel &&
      buttonUrl && (
        <div className="absolute left-5 top-5 md:bottom-5 md:top-[unset]">
          <div>
            <Link
              href={buttonUrl}
              className="flex w-max flex-row flex-nowrap items-center justify-start rounded-lg bg-[#30688b80] px-2.5 leading-5 text-white no-underline hover:bg-[#30688b] hover:duration-[0.5s] md:mt-[0.51rem]"
            >
              {/*prettier-ignore*/}
              <p className="font-acumin text-[16px] leading-standard tracking-standard md:text-[18px]">
                {buttonLabel}
              </p>
              <span className="translate-x-[30%] duration-[0.5s] hover:ease-in">
                <SvgArrow className="pt-0.5 *:fill-white" />
              </span>
            </Link>
          </div>
        </div>
      )
    );
  };

  const getWaveOutline = () => {
    return (
      <div
        className={classNames(
          `hidden md:absolute md:bottom-0 md:z-[1] md:block md:w-full`,
          {
            [heroWaveSizeMap(heroWaveOutlineHeight)]: heroWaveOutlineHeight,
          }
        )}
      >
        <svg
          x="0px"
          y="0px"
          width="1440px"
          height="136px"
          viewBox="0 0 1440 136"
          preserveAspectRatio="none"
          className="absolute left-0 top-0 block h-full w-full"
        >
          <path
            d="M818.9,21C499.6-47.8,139.9,69,0,136h1440V80.5C1408.9,89.5,1218.1,107,818.9,21z"
            style={{ fill: '#fff' }}
          />
        </svg>
      </div>
    );
  };

  const isSvgImage = image?.desktop?.contentType === 'image/svg+xml';

  return (
    <div
      className={classNames({
        [bgColorMap(backgroundColor)]: backgroundColor,
      })}
    >
      <div className="relative overflow-hidden">
        {video?.id && video?.host === 'wistia' ? (
          <div
            className={classNames('m-auto max-w-[1800px]', {
              ['mdDesktop:w-[unset]']: isHeroTheaterMode,
              ['mdDesktop:w-1/2']: !isHeroTheaterMode,
              [videoHeroSizeMap(videoMaxHeight!)]: videoMaxHeight,
            })}
          >
            <WistiaVideoBanner videoId={video?.id} />
            {isHeroWaveOutline && getWaveOutline()}
          </div>
        ) : (
          image?.desktop?.url && (
            <div
              className={classNames(
                'flex max-h-[30vh] flex-col justify-center pb-0 md:max-h-[50vh]',
                {
                  ['items-center md:pb-[140px]']: isSvgImage,
                }
              )}
            >
              <picture className="flex flex-col justify-center">
                <source
                  srcSet={composeImageUrl({ image: image.desktop, w: 2700 })}
                  media="(min-width: 1024px)"
                  content={image.desktop?.contentType}
                />
                <source
                  srcSet={composeImageUrl({ image: image.tablet, w: 1024 })}
                  media="(min-width: 768px)"
                  content={image.tablet?.contentType}
                />
                <img
                  srcSet={composeImageUrl({ image: image.mobile, w: 800 })}
                  alt={image.mobile?.title}
                  content={image.mobile?.contentType}
                  className="h-full"
                  style={
                    isSvgImage
                      ? {
                          width: '80vw',
                          padding: '80px 0',
                          maxWidth: '900px',
                        }
                      : {}
                  }
                />
              </picture>
              {isHeroWaveOutline && getWaveOutline()}
            </div>
          )
        )}
        {getTextOverlay()}
        {getCTAButton()}
      </div>
      {/* Storybook requires the base video script. */}
      {isStoryBook && <WistiaVideoBase />}
    </div>
  );
};

export default Hero;
